import type { Locale } from '@nolens/i18n';
/**
 * Contrato da API (apps/api) partilhado pela app web e pela extensão.
 * Só tipos e funções puras: a validação de entrada vive na API (zod).
 */
import { hasPii } from './pii';
import type { Box, GuideSite, GuideSource, Step } from './steps';

export type ErrorCode =
  | 'UNAUTHENTICATED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'VALIDATION_FAILED'
  | 'CONFLICT'
  | 'VERSION_CONFLICT'
  | 'PAYLOAD_TOO_LARGE'
  | 'RATE_LIMITED'
  | 'UPSTREAM_UNAVAILABLE'
  | 'NOT_CONFIGURED'
  | 'PLAN_LIMIT'
  | 'EXTENSION_OUTDATED'
  | 'APP_OUTDATED'
  | 'INTERNAL';

export interface ApiErrorBody {
  error: { code: ErrorCode; message: string; requestId?: string; fields?: Record<string, string> };
}

export type Role = 'owner' | 'editor' | 'viewer';

export interface WorkspaceSummary {
  id: string;
  name: string;
  kind: 'personal' | 'team';
  role: Role;
  members: number;
  guides: number;
  /** Plano efetivo do espaço (plans.ts). */
  plan: import('./plans').PlanId;
  /** As gravações dos membros revêem-se antes de irem para a conta (decide o dono). */
  reviewRequired: boolean;
  /** O texto escrito nos campos é guardado (false: nem nas gravações dos membros). */
  keepTyped: boolean;
  /** Cor principal do kit de marca do espaço (null = sem kit: a cor Nolens). */
  accent: string | null;
}

/** PATCH /v1/workspaces/:id (só o dono). */
export interface PatchWorkspaceRequest {
  name?: string;
  reviewRequired?: boolean;
  keepTyped?: boolean;
}

/** Passos sem o texto escrito nos campos (política do espaço «não guardar o texto escrito»). */
export function withoutTyped(steps: Step[]): Step[] {
  return steps.map((s) => {
    if (s.typed === undefined) return s;
    const { typed: _typed, ...rest } = s;
    return rest;
  });
}

export interface MeResponse {
  user: { id: string; email: string | null };
  /** Língua da interface escolhida pela pessoa (null = a do browser). */
  locale: Locale | null;
  /** Proprietário da aplicação (verificado na base de dados): acesso ao portal de administração. */
  isPlatformAdmin: boolean;
  /** Link da extensão na Chrome Web Store (definido no portal); null enquanto não houver. */
  extensionUrl: string | null;
  /** Link para descarregar a app de Mac (definido no portal); null enquanto não houver. */
  macAppUrl: string | null;
  /** A app de Mac ligada a esta conta (null: nunca foi ligada). */
  macApp: MacAppState | null;
  personalWorkspaceId: string;
  workspaces: WorkspaceSummary[];
}

/** A app de Mac de uma pessoa: a versão e quando foi ligada e vista pela última vez. */
export interface MacAppState {
  version: string;
  linkedAt: string;
  lastSeenAt: string;
}

export type Visibility = 'workspace' | 'link';

export interface GuideSummary {
  id: string;
  workspaceId: string;
  title: string;
  description: string;
  steps: number;
  actions: number;
  /** Primeira captura (para a miniatura da lista). */
  cover: Pick<Step, 'imageId' | 'viewport' | 'point' | 'box' | 'zoom' | 'crop' | 'kind'> | null;
  visibility: Visibility;
  version: number;
  createdAt: string;
  updatedAt: string;
  /** Quem gravou o guia (null se já saiu do espaço ou apagou a conta). */
  authorEmail: string | null;
  /** Primeira aplicação usada no guia (nome e ícone guardados ao gravar). */
  site: GuideSite | null;
  /** Endereço assinado da miniatura (ver RemoteGuide.imageAccess). */
  imageAccess?: Record<string, string>;
}

export interface RemoteGuide {
  id: string;
  workspaceId: string;
  title: string;
  description: string;
  /** Língua do guia (títulos automáticos, exportações, página pública). */
  language: Locale;
  /** Onde foi gravado: na extensão ('web') ou na app de Mac. */
  source: GuideSource;
  steps: Step[];
  visibility: Visibility;
  shareToken: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
  recordedAt: string | null;
  canEdit: boolean;
  /** A pessoa recebe avisos quando o guia muda ou é comentado. */
  subscribed: boolean;
  /**
   * Parâmetros dos endereços assinados das capturas (imageId → «e=…&s=…»), para as ler sem
   * sessão em GET /v1/guides/:id/images/:imageId. Ausente quando a API não os assina.
   */
  imageAccess?: Record<string, string>;
}

/** PUT /v1/guides/:id — publicar a partir da extensão (cria ou substitui). */
export interface PutGuideRequest {
  /** Espaço onde criar (por omissão, o pessoal). Ignorado se o guia já existir. */
  workspaceId?: string;
  title: string;
  description: string;
  /** Língua do guia (por omissão, inglês). */
  language?: Locale;
  /** Onde foi gravado (por omissão, 'web'). Ignorado se o guia já existir. */
  source?: GuideSource;
  steps: Step[];
  recordedAt?: number;
  /**
   * Versão que a extensão conhece (a do envio anterior da mesma gravação); se a web tiver uma
   * mais recente devolve VERSION_CONFLICT, e se o guia já não existir, NOT_FOUND (não volta a
   * criá-lo). null = o primeiro envio: cria, ou substitui.
   */
  baseVersion?: number | null;
}

export interface PutGuideResponse {
  id: string;
  workspaceId: string;
  version: number;
  created: boolean;
  /** Capturas que a API ainda não tem: a extensão envia só estas. */
  missingImages: string[];
}

/** PATCH /v1/guides/:id — alterações na web. */
export interface PatchGuideRequest {
  baseVersion: number;
  title?: string;
  description?: string;
  steps?: Step[];
  visibility?: Visibility;
  workspaceId?: string;
  language?: Locale;
}

/** POST /v1/guides/:id/copy: guardar o rascunho do editor como um guia novo (num conflito). */
export interface CopyGuideRequest {
  title: string;
  description: string;
  language?: Locale;
  steps: Step[];
}

export interface PublicGuide {
  id: string;
  title: string;
  description: string;
  language: Locale;
  steps: Step[];
  updatedAt: string;
  workspaceName: string;
  /** Kit de marca do espaço (null = marca Nolens). */
  brand: BrandSettings | null;
  /** O plano do espaço permite incorporar o guia noutras páginas (embed). */
  embed: boolean;
}

export interface Member {
  userId: string;
  email: string | null;
  role: Role;
  you: boolean;
}

export interface InviteCreated {
  id: string;
  token: string;
  role: Exclude<Role, 'owner'>;
  expiresAt: string;
  /** Para quem foi enviado por email (null = só a ligação). */
  email: string | null;
  /** O email foi aceite pelo serviço de envio (false sem email, sem envio configurado ou falha). */
  emailed: boolean;
}

export interface InviteSummary {
  id: string;
  role: Exclude<Role, 'owner'>;
  createdAt: string;
  expiresAt: string;
  /** Enviado por email para este endereço (null = só a ligação). */
  email: string | null;
}

export interface InvitePreview {
  workspaceName: string;
  role: Exclude<Role, 'owner'>;
}

export interface ExtensionSession {
  accessToken: string;
  refreshToken: string;
  /** Segundos desde 1970. */
  expiresAt: number;
}

/**
 * POST /v1/extension/link — a extensão recebe uma sessão sua (não partilha a da web,
 * para que a rotação dos refresh tokens de uma não termine a outra).
 * Em produção vem um token de uso único que a extensão troca no Supabase Auth.
 */
export type ExtensionLink =
  | { kind: 'session'; email: string | null; session: ExtensionSession }
  | {
      kind: 'token_hash';
      email: string;
      tokenHash: string;
      supabaseUrl: string;
      publishableKey: string;
    };

/**
 * Ligação da app de Mac (ADR-G25, PKCE). A app gera um `verifier` e abre a web com o
 * `challenge` = base64url(SHA-256(verifier)); a web pede o código (POST /v1/mac/link) e
 * devolve-o à app pelo URL scheme; a app troca-o, com o `verifier`, por um ExtensionLink
 * (POST /public/mac/link/redeem) e cria a sua sessão no Supabase Auth.
 */
export interface MacLinkRequest {
  challenge: string;
}

export interface MacLinkCode {
  code: string;
  expiresAt: string;
}

export interface MacRedeemRequest {
  code: string;
  verifier: string;
}

/** POST /v1/mac/seen: a app de Mac, ao arrancar e de hora a hora (estado na página «App para Mac»). */
export interface MacSeenRequest {
  version: string;
}

/** URL scheme da app de Mac (nolens-guides://ligar, gravar, guiar?guia=…, executar?guia=…). */
export const MAC_URL_SCHEME = 'nolens-guides';

/** Mensagem da app web para a extensão (via a ponte na página). */
export interface LinkMessage {
  link: ExtensionLink;
  apiUrl: string;
  webUrl: string;
}

/**
 * Passo como fica na conta: sem a captura original (pode ter dados que o autor desfocou)
 * e sem as zonas de dados pessoais, porque a extensão já as tapou na imagem enviada
 * (exceto se o autor decidiu mantê-las).
 */
export function publishableStep(step: Step): Step {
  const { originalImageId: _o, noShot: _n, pii, piiDecision, ...rest } = step;
  const out: Step = fitStep({ ...rest, url: cleanUrl(step.url) });
  if (step.target?.sensitive) {
    delete out.typed;
    delete out.value;
  }
  // Texto escrito com dados pessoais não vai para a conta (só servia para o título)
  if (hasPii(out.typed)) delete out.typed;
  // App de Mac: títulos de janela com dados pessoais (um email aberto, um documento) também não
  if (out.window && hasPii(out.window.title)) delete out.window;
  if (out.target?.ax?.window && hasPii(out.target.ax.window)) {
    const { window: _w, ...ax } = out.target.ax;
    out.target = { ...out.target, ax };
  }
  if (!pii?.length) return out;
  return { ...out, piiDecision: piiDecision === 'kept' ? 'kept' : 'hidden' };
}

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
const finite = (...v: number[]) => v.every(Number.isFinite);

/**
 * Caixa (fração do ecrã) cortada à parte visível; sem nada visível, nenhuma. O elemento à
 * volta de um clique pode ser um bloco enorme (uma lista, um artigo inteiro) que começa
 * muito acima do ecrã: a caixa toda não serve para o zoom e a API não a aceita.
 */
export function visibleBox(b: Box | undefined): Box | undefined {
  if (!b || !finite(b.x, b.y, b.w, b.h)) return undefined;
  const x = clamp01(b.x);
  const y = clamp01(b.y);
  const w = clamp01(b.x + b.w) - x;
  const h = clamp01(b.y + b.h) - y;
  return w > 0 && h > 0 ? { x, y, w, h } : undefined;
}

const cut = (v: string | undefined, max: number) => (typeof v === 'string' ? v.slice(0, max) : v);

/**
 * O passo dentro dos limites que a API aceita (apps/api/src/schemas.ts): geometria no ecrã
 * e textos até ao tamanho máximo. Um passo fora dos limites fazia recusar o guia inteiro.
 */
function fitStep(step: Step): Step {
  const out: Step = {
    ...step,
    title: step.title.slice(0, 300),
    description: step.description.slice(0, 5000),
    pageTitle: step.pageTitle.slice(0, 500),
    url: step.url.slice(0, 4000),
  };
  const box = visibleBox(step.box);
  if (box) out.box = box;
  else delete out.box;
  if (step.point) {
    if (finite(step.point.x, step.point.y))
      out.point = { x: clamp01(step.point.x), y: clamp01(step.point.y) };
    else delete out.point;
  }
  if (step.crop) {
    const crop = visibleBox(step.crop);
    if (crop) out.crop = crop;
    else delete out.crop;
  }
  if (out.typed !== undefined) out.typed = cut(out.typed, 2000);
  if (step.option)
    out.option = { text: step.option.text.slice(0, 500), value: step.option.value.slice(0, 500) };
  if (step.tab) out.tab = { title: step.tab.title.slice(0, 500) };
  if (step.site?.name) out.site = { ...step.site, name: step.site.name.slice(0, 80) };
  if (step.app)
    out.app = {
      ...step.app,
      bundleId: step.app.bundleId.slice(0, 200),
      name: step.app.name.slice(0, 80),
    };
  if (step.window) out.window = { title: step.window.title.slice(0, 300) };
  if (step.menu) out.menu = step.menu.slice(0, 10).map((m) => m.slice(0, 200));
  if (step.shortcut)
    out.shortcut = { key: step.shortcut.key.slice(0, 20), mods: step.shortcut.mods.slice(0, 4) };
  const t = step.target;
  if (t) {
    out.target = {
      ...t,
      selectors: t.selectors.filter((s) => s.length <= 1000).slice(0, 20),
      framePath: t.framePath
        .slice(0, 10)
        .map((i) => Math.min(1000, Math.max(0, Math.round(i) || 0))),
      shadowPath: t.shadowPath.filter((s) => s.length <= 1000).slice(0, 10),
    };
    for (const [k, max] of [
      ['tag', 40],
      ['role', 60],
      ['text', 500],
      ['ariaLabel', 500],
      ['title', 500],
      ['name', 200],
      ['placeholder', 500],
      ['type', 40],
      ['testId', 200],
      ['label', 500],
    ] as const) {
      const v = out.target[k];
      if (typeof v === 'string') out.target[k] = v.slice(0, max);
    }
    const r = t.rect;
    if (r && !finite(r.xPct, r.yPct, r.wPct, r.hPct)) delete out.target.rect;
    if (t.ax) {
      const ax = { ...t.ax, path: t.ax.path.slice(0, 40).map((p) => p.slice(0, 100)) };
      for (const [k, max] of [
        ['bundleId', 200],
        ['role', 60],
        ['subrole', 60],
        ['title', 500],
        ['description', 500],
        ['identifier', 200],
        ['window', 300],
      ] as const) {
        const v = ax[k];
        if (typeof v === 'string') ax[k] = v.slice(0, max);
      }
      out.target.ax = ax;
    }
  }
  return out;
}

/** Endereço sem query nem fragmento: podem levar códigos de sessão ou dados pessoais. */
export function cleanUrl(url: string): string {
  try {
    const u = new URL(url);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return '';
    return u.origin + u.pathname;
  } catch {
    return '';
  }
}

/** As zonas a tapar na imagem antes de a enviar. */
export function piiToMask(step: Step): NonNullable<Step['pii']> {
  return step.piiDecision === 'kept' ? [] : (step.pii ?? []);
}

// ── Fase 3 ────────────────────────────────────────────────────────────────

/** GET/PUT /v1/workspaces/:id/brand */
export interface BrandSettings {
  accent: string;
  font: import('./brand-kit').FontChoice;
  footer: string;
  /** Nome da empresa nos cabeçalhos e na capa das exportações ('' = o nome do espaço). */
  companyName: string;
  /** Cor da capa e do fecho (null = automática, a cor principal muito escura). */
  coverColor: string | null;
  /** Cor de destaque (régua e data na capa; null = automática, a cor principal clara). */
  highlightColor: string | null;
  /** Versão do logótipo enviado (null = sem logótipo). */
  logoVersion: number | null;
  fontVersion: number | null;
}

export type PublicBrand = BrandSettings;

export interface CommentItem {
  id: string;
  stepId: string | null;
  authorEmail: string | null;
  body: string;
  createdAt: string;
  resolvedAt: string | null;
  mine: boolean;
}

export interface GuideStats {
  total: number;
  last30: { day: string; member: number; public: number; embed: number }[];
}

/** Resumo da pessoa no Início: os guias que gravou, em todos os espaços onde está. */
export interface MeSummary {
  guides: number;
  steps: number;
  /** Poupança estimada (savings.ts), em segundos. */
  savedSeconds: number;
  reads30: number;
  readsTotal: number;
}

/** Leituras de um espaço (todos os membros veem): por dia e os guias mais lidos. */
export interface WorkspaceStats {
  guides: number;
  readsTotal: number;
  last30: GuideStats['last30'];
  top: { guideId: string; title: string; reads30: number; total: number }[];
}

export interface NotificationItem {
  id: string;
  guideId: string;
  guideTitle: string;
  kind: 'updated' | 'comment';
  actorEmail: string | null;
  createdAt: string;
  read: boolean;
}

/** POST /v1/guides/:id/suggest — propostas de texto (o autor escolhe o que aplica). */
export interface SuggestResponse {
  title: string;
  description: string;
  /** description: só para passos sem descrição, e só quando acrescenta algo ao título. */
  steps: { id: string; title: string; description?: string }[];
}

/** Descrição sugerida de um passo: uma frase curta, que cabe nos layouts (PDF, PowerPoint). */
export const SUGGESTED_STEP_DESCRIPTION_MAX = 140;

// ── Administração (proprietário) ──────────────────────────────────────────

/** GET /public/policy — regras dos sites (a extensão lê-as mesmo sem conta ligada). */
export interface PolicyResponse {
  rules: import('./policy').SiteRule[];
  updatedAt: string | null;
}

export interface AdminRule {
  id: string;
  pattern: string;
  mode: import('./policy').SiteMode;
  note: string;
  updatedAt: string;
}

export interface AuditEntry {
  id: string;
  at: string;
  actorEmail: string | null;
  action:
    | 'rule.create'
    | 'rule.update'
    | 'rule.delete'
    | 'site.unlock'
    | 'site.relock'
    | 'grant.pro'
    | 'grant.revoke'
    | 'grant.team'
    | 'beta.settings'
    | 'beta.welcome';
  target: string;
  detail: Record<string, unknown>;
}

/** GET /v1/admin/grants: testes do Pro dados pelo proprietário (sem pagamento). */
export interface ProGrant {
  email: string;
  status: 'active' | 'trialing' | 'past_due' | 'canceled';
  trialEndsAt: string | null;
  updatedAt: string;
}

/** POST /v1/admin/grants/pro e /grants/team. */
export interface GrantRequest {
  email: string;
  days: number;
}
export interface GrantResult {
  until: string;
  /** Só no prolongamento do teste da equipa: quantas equipas da pessoa. */
  teams?: number;
  /** A equipa foi criada agora (a pessoa não tinha nenhuma). */
  created?: boolean;
}

/** POST /public/beta-signups: inscrição para testar a versão de teste (página /beta). */
export interface BetaSignupRequest {
  email: string;
  name?: string;
  company?: string;
  role?: string;
  /** «1», «2-5», «6-20», «21-100», «100+». */
  teamSize?: string;
  /** Área ou nicho: contabilidade, RH, apoio ao cliente… */
  area?: string;
  /** Aplicações que usam no browser (ERP, CRM, portais…). */
  tools?: string;
  locale?: import('@nolens/i18n').Locale;
}

/** GET /v1/admin/beta-signups (só o proprietário da aplicação). */
export interface BetaSignup extends Required<Omit<BetaSignupRequest, 'locale'>> {
  id: string;
  locale: string;
  createdAt: string;
  /** Já entrou em guides.nolens.app com este email? */
  hasAccount: boolean;
  /** Boas-vindas (link da extensão) enviadas: a última vez e quantas. */
  welcomeSentAt: string | null;
  welcomeCount: number;
  /** Ligação pessoal para reportar anomalias. */
  reportUrl: string;
  /** Pro ativo (dado à mão ou pago) e até quando; idem para as equipas de que é dona. */
  proLive: boolean;
  proUntil: string | null;
  teamLive: boolean;
  teamUntil: string | null;
  /** Anomalias reportadas. */
  reports: number;
}

/** POST /v1/admin/grants/team com create: sem equipa, cria uma (a pessoa fica dona). */
export interface TeamGrantRequest extends GrantRequest {
  create?: boolean;
  teamName?: string;
}

export interface BetaWelcomeText {
  subject: string;
  body: string;
}

/**
 * GET/PUT /v1/admin/beta/settings: link da extensão e o texto de boas-vindas por língua.
 * No texto: {nome}, {email}, {link_extensao}, {link_entrar} e {link_reportar}.
 */
export interface BetaSettings {
  extensionUrl: string;
  /** Descarga da app de Mac (DMG), mostrada na página «App para Mac». */
  macUrl: string;
  welcome: Record<Locale, BetaWelcomeText>;
}
/** O GET devolve também os textos por omissão (para repor). */
export interface BetaSettingsResponse extends BetaSettings {
  defaults: Record<Locale, BetaWelcomeText>;
  updatedAt: string;
}

/** POST /v1/admin/beta/welcome: a estas inscrições, ou a todas as que ainda não o receberam. */
export interface BetaWelcomeRequest {
  ids?: string[];
  pending?: boolean;
  /** Só um teste, para o email de quem envia (com os dados da primeira inscrição). */
  test?: boolean;
}
export interface BetaWelcomeResult {
  sent: number;
  failed: number;
}

export type BetaSeverity = 'blocker' | 'annoying' | 'suggestion';
export type BetaReportStatus = 'new' | 'reviewing' | 'resolved';

/** GET /public/beta-testers/:token: quem está a reportar (ligação pessoal). */
export interface BetaTester {
  email: string;
  name: string;
}

/**
 * POST /public/beta-reports (multipart/form-data): estes campos e até 5 ficheiros «files»
 * (PNG, JPEG, WebP ou PDF, até 10 MB cada e 25 MB no total).
 */
export interface BetaReportRequest {
  token?: string;
  email?: string;
  title: string;
  description: string;
  steps?: string;
  severity: BetaSeverity;
  pageUrl?: string;
  browser?: string;
}

export interface BetaReportFile {
  id: string;
  name: string;
  contentType: 'image/png' | 'image/jpeg' | 'image/webp' | 'application/pdf';
  bytes: number;
}

/** GET /v1/admin/beta/reports */
export interface BetaReport {
  id: string;
  email: string;
  name: string;
  title: string;
  description: string;
  steps: string;
  severity: BetaSeverity;
  pageUrl: string;
  browser: string;
  status: BetaReportStatus;
  adminNote: string;
  createdAt: string;
  updatedAt: string;
  files: BetaReportFile[];
}

/** PATCH /v1/admin/beta/reports/:id */
export interface BetaReportPatch {
  status?: BetaReportStatus;
  adminNote?: string;
}

/** Poupança da plataforma por mês (só totais: sem espaços nem pessoas). */
export interface PlatformSavingsMonth {
  month: string;
  guides: number;
  steps: number;
}

/**
 * GET /v1/workspaces/:id/savings — poupança estimada de um espaço, para o dono desse espaço
 * (por mês e por pessoa). O proprietário da aplicação não tem esta vista.
 */
export interface WorkspaceSavings {
  steps: number;
  guides: number;
  savedSeconds: number;
  byMonth: { month: string; guides: number; steps: number }[];
  byMember: { userId: string | null; email: string | null; guides: number; steps: number }[];
}

export interface AdminOverview {
  users: number;
  workspaces: number;
  teams: number;
  guides: number;
  steps: number;
  /** Poupança estimada (passos × 120 s), em segundos. */
  savedSeconds: number;
  rules: number;
  unlocks30d: number;
}
